import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { IconButton, IconTile, useEscapeLayer } from '@/design-system';
import { now } from '@/lib/clock';
import { cx } from '@/lib/cx';
import { formatRelative } from '@/lib/format';
import { alertMeta, alertPath, alertTitle, jobKind } from '@/features/reports/alerts';
import { markSeen, notificationsKey, notificationsQuery, type NotificationOut } from '@/features/reports/api';
import s from './notifications.module.css';

type Tv = (k: string, o?: Record<string, unknown>) => string;

/**
 * The header bell (P6-E02, additive): the design's 38 px button with its orange dot — now only while something
 * is unread — and a dropdown in the design's card language: «Уведомления», then one row per notification (the
 * alert's or the finished job's icon tile, headline, sub-line and when), the ones unread at opening tinted with
 * the accent dot. Opening the bell reads everything (`POST /admin/notifications/seen`); a row opens its screen.
 * Esc and an outside click close it.
 */
export function NotificationsBell({ className, dotClassName }: { className?: string; dotClassName?: string }) {
  const { t } = useTranslation('shell');
  const { t: td } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const tv = td as unknown as Tv;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery(notificationsQuery);
  const [open, setOpen] = useState(false);
  // What was unread when the bell opened: the dots stay for this look, the server already counts them read.
  const [shown, setShown] = useState<NotificationOut[] | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const close = () => {
    setOpen(false);
    setShown(null);
  };
  useEscapeLayer(open, close);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const unread = q.data?.unread ?? 0;
  const items = shown ?? q.data?.items ?? [];
  const toggle = () => {
    if (open) return close();
    setOpen(true);
    setShown(q.data?.items ?? null);
    if (unread > 0)
      void markSeen()
        .then((next) => qc.setQueryData(notificationsKey, next))
        .catch(() => qc.invalidateQueries({ queryKey: notificationsKey }));
  };
  const openOne = (n: NotificationOut) => {
    close();
    const to = alertPath(n.kind, n.target ?? {});
    if (to) navigate(to);
  };
  const subOf = (n: NotificationOut) => {
    if (!jobKind(n.kind)) return n.subtitle;
    return n.subtitle ? (tc as unknown as Tv)(`errors.${n.subtitle}`, { defaultValue: n.subtitle }) : null;
  };

  return (
    <div className={s.root} ref={root}>
      <IconButton
        icon="bell"
        label={unread ? t('notifications_unread', { count: unread }) : t('notifications')}
        tone="header"
        size={38}
        className={className}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        {unread > 0 && <span className={dotClassName} />}
      </IconButton>
      {open && (
        <div className={s.panel} role="dialog" aria-label={t('notifications')}>
          <div className={s.head}>
            <span className={s.title}>{t('notifications')}</span>
            <span className={s.hint}>{t('notifications_hint')}</span>
          </div>
          {q.isError ? (
            <div className={s.empty}>{t('notifications_error')}</div>
          ) : items.length === 0 ? (
            <div className={s.empty}>{t('notifications_empty')}</div>
          ) : (
            <ul className={s.list}>
              {items.map((n) => {
                const target = n.target ?? {};
                const meta = alertMeta(n.kind, target, n.severity);
                const sub = subOf(n);
                return (
                  <li key={n.id}>
                    <button type="button" className={cx(s.row, n.unread && s.unread)} onClick={() => openOne(n)}>
                      <IconTile icon={meta.icon} size={34} iconSize={15} />
                      <span className={s.body}>
                        <span className={s.rowTitle}>{alertTitle(n.kind, n.title, target, tv)}</span>
                        {sub && <span className={s.sub}>{sub}</span>}
                        {n.at && <span className={s.when}>{formatRelative(n.at, now(), true)}</span>}
                      </span>
                      {n.unread && <span className={s.dot} aria-label={t('notifications_new')} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
